import { useState, useEffect } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useGame } from './GameContext';
import APIService from '../APIService';
import './PatientRoom.css';

export default function PatientRoom() {
  const { caseId } = useParams();
  const [searchParams] = useSearchParams();
  const isReview = searchParams.get('review') === 'true';
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const upper = (str) => str.toLocaleUpperCase(i18n.language);
  const { balance, completedCases, deductBalance, applySubmissionResult,
          tests, diagnoses, backgroundQuestions, physicalExamItems, levels, caseReward,
          canPlay,
          gameDataLoading, gameDataError } = useGame();

  const [askedQuestions, setAskedQuestions] = useState(new Set());
  const [selectedQuestionId, setSelectedQuestionId] = useState('');
  const [askedExamItems, setAskedExamItems] = useState(new Set());
  const [selectedExamItemId, setSelectedExamItemId] = useState('');
  const [orderedTests, setOrderedTests] = useState([]);
  const [selectedTestId, setSelectedTestId] = useState('');
  const [testResultsMap, setTestResultsMap] = useState({});
  const [backgroundAnswersMap, setBackgroundAnswersMap] = useState({});
  const [examAnswersMap, setExamAnswersMap] = useState({});
  const [selectedDiagnosis, setSelectedDiagnosis] = useState('');
  const [diagnosisSearch, setDiagnosisSearch] = useState('');
  const [diagnosisOpen, setDiagnosisOpen] = useState(false);
  const [phase, setPhase] = useState('examine'); // 'examine' | 'result'
  const [result, setResult] = useState(null);
  const [confirmTest, setConfirmTest] = useState(null);
  const [confirmQuestion, setConfirmQuestion] = useState(null);
  const [confirmExamItem, setConfirmExamItem] = useState(null);
  const [infoTest, setInfoTest] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);

  useEffect(() => {
    if (!isReview || !backgroundQuestions || !physicalExamItems) return;
    backgroundQuestions.forEach(async (q) => {
      try {
        const { answer } = await APIService.getBackgroundAnswer(caseId, q.id);
        setBackgroundAnswersMap((prev) => ({ ...prev, [q.id]: answer }));
      } catch (err) {
        console.error('Failed to fetch background answer:', err);
      }
    });
    physicalExamItems.forEach(async (item) => {
      try {
        const { answer } = await APIService.getPhysicalExamAnswer(caseId, item.id);
        setExamAnswersMap((prev) => ({ ...prev, [item.id]: answer }));
      } catch (err) {
        console.error('Failed to fetch physical exam answer:', err);
      }
    });
  }, [isReview, caseId, backgroundQuestions, physicalExamItems]); // eslint-disable-line react-hooks/exhaustive-deps

  if (gameDataLoading) {
    return (
      <div className="room-error">
        <p>{t('patient.loadingGameData')}</p>
      </div>
    );
  }

  if (gameDataError) {
    return (
      <div className="room-error">
        <p>{t('patient.failedToLoad')} {gameDataError}</p>
        <button onClick={() => navigate('/game')}>{t('patient.backToLobby')}</button>
      </div>
    );
  }

  const patient = levels.find((p) => p.id === caseId);
  const alreadyDone = completedCases[caseId]?.correct === true;

  if (!patient) {
    return (
      <div className="room-error">
        <p>{t('patient.patientNotFound')}</p>
        <button onClick={() => navigate('/game')}>{t('patient.backToLobby')}</button>
      </div>
    );
  }

  if (alreadyDone && !isReview) {
    navigate('/game');
    return null;
  }

  const testList = Object.values(tests);
  const totalTestCost = orderedTests.reduce((sum, id) => sum + tests[id].cost, 0);

  const isQuestionAsked = (qId) => isReview || askedQuestions.has(qId);
  const isExamItemAsked = (id) => isReview || askedExamItems.has(id);

  const formatMoney = (n) =>
    n < 0 ? `-${Math.abs(n).toLocaleString()}` : `${n.toLocaleString()}`;

  const isMale = patient.gender === 'Male' || patient.gender === 'Erkek';

  const handleOrderTestFromDropdown = () => {
    if (!selectedTestId) return;
    setSelectedTestId('');
    if (isReview) {
      fetchTestResult(selectedTestId);
    } else {
      setConfirmTest(selectedTestId);
    }
  };

  const fetchTestResult = async (testId) => {
    setOrderedTests((prev) => [...prev, testId]);
    try {
      const { result: testResult } = await APIService.getTestResult(caseId, testId);
      setTestResultsMap((prev) => ({ ...prev, [testId]: testResult }));
    } catch (err) {
      console.error('Failed to fetch test result:', err);
    }
  };

  const confirmOrderTest = async () => {
    const testId = confirmTest;
    setConfirmTest(null);
    const cost = tests[testId].cost;
    if (balance - cost < -5000) {
      alert(t('patient.insufficientFunds'));
      return;
    }
    deductBalance(cost);
    await fetchTestResult(testId);
  };

  const handleRevealAnswer = async () => {
    if (submitting) return;
    setSubmitting(true);
    setSubmitError(null);
    try {
      const { correctDiagnosis, explanation } = await APIService.getCaseAnswer(caseId);
      setResult({ correct: true, correctDiagnosis, explanation, earned: 0, irrelevantCost: 0, isReview: true });
      setPhase('result');
    } catch (err) {
      setSubmitError(err.message || t('patient.submitFailed'));
    } finally {
      setSubmitting(false);
    }
  };

  const handleSubmitDiagnosis = async () => {
    if (!selectedDiagnosis || submitting) return;
    setSubmitting(true);
    setSubmitError(null);
    try {
      const response = await APIService.submitDiagnosis(caseId, selectedDiagnosis, orderedTests, [...askedQuestions], [...askedExamItems]);
      applySubmissionResult(response);
      setResult(response);
      setPhase('result');
    } catch (err) {
      console.error('Failed to submit diagnosis:', err);
      setSubmitError(err.message || t('patient.submitFailed'));
    } finally {
      setSubmitting(false);
    }
  };

  const filteredDiagnoses = diagnoses.filter((d) =>
    d.toLowerCase().includes(diagnosisSearch.toLowerCase())
  );

  const handleDiagnosisSelect = (d) => {
    setSelectedDiagnosis(d);
    setDiagnosisSearch(d);
    setDiagnosisOpen(false);
  };

  const QUESTION_COST = 50;
  const EXAM_COST = 50;

  const handleAskQuestion = () => {
    if (!selectedQuestionId) return;
    setConfirmQuestion(selectedQuestionId);
    setSelectedQuestionId('');
  };

  const confirmAskQuestion = async () => {
    const qId = confirmQuestion;
    setConfirmQuestion(null);
    setAskedQuestions((prev) => new Set([...prev, qId]));
    try {
      const { answer } = await APIService.getBackgroundAnswer(caseId, qId);
      setBackgroundAnswersMap((prev) => ({ ...prev, [qId]: answer }));
    } catch (err) {
      console.error('Failed to fetch background answer:', err);
    }
  };

  const handleAskExamItem = () => {
    if (!selectedExamItemId) return;
    setConfirmExamItem(selectedExamItemId);
    setSelectedExamItemId('');
  };

  const confirmAskExamItem = async () => {
    const id = confirmExamItem;
    setConfirmExamItem(null);
    setAskedExamItems((prev) => new Set([...prev, id]));
    try {
      const { answer } = await APIService.getPhysicalExamAnswer(caseId, id);
      setExamAnswersMap((prev) => ({ ...prev, [id]: answer }));
    } catch (err) {
      console.error('Failed to fetch physical exam answer:', err);
    }
  };

  const handleTryAgain = () => {
    setAskedQuestions(new Set());
    setSelectedQuestionId('');
    setAskedExamItems(new Set());
    setSelectedExamItemId('');
    setOrderedTests([]);
    setSelectedTestId('');
    setTestResultsMap({});
    setBackgroundAnswersMap({});
    setExamAnswersMap({});
    setSelectedDiagnosis('');
    setDiagnosisSearch('');
    setDiagnosisOpen(false);
    setPhase('examine');
    setResult(null);
  };

  if (phase === 'result') {
    return (
      <div className="room-result-overlay">
        <div className={`room-result-card ${result.correct ? 'result-win' : 'result-loss'}`}>
          <div className="result-icon">{result.correct ? '✅' : '❌'}</div>
          <h2>{result.correct ? t('patient.correctDiagnosis') : t('patient.incorrectDiagnosis')}</h2>
          {result.correct ? (
            <>
              <p className="result-diagnosis-label">
                {t('patient.diagnosis')} <strong>{result.correctDiagnosis}</strong>
              </p>
              {!result.isReview && (
                <div className="result-breakdown">
                  {result.irrelevantCost > 0 && (
                    <div className="breakdown-row">
                      <span>{t('patient.irrelevantTestsCost')}</span>
                      <span className="lose">-{formatMoney(result.irrelevantCost)}</span>
                    </div>
                  )}
                  {result.irrelevantHistoryCost > 0 && (
                    <div className="breakdown-row">
                      <span>{t('patient.irrelevantHistoryCost')}</span>
                      <span className="lose">-{formatMoney(result.irrelevantHistoryCost)}</span>
                    </div>
                  )}
                  {result.irrelevantExamCost > 0 && (
                    <div className="breakdown-row">
                      <span>{t('patient.irrelevantExamCost')}</span>
                      <span className="lose">-{formatMoney(result.irrelevantExamCost)}</span>
                    </div>
                  )}
                  <div className="breakdown-row total">
                    <span>{t('patient.totalEarned')}</span>
                    <span className="earn">{formatMoney(result.earned)}</span>
                  </div>
                </div>
              )}
              <p className="result-explanation">{result.explanation}</p>
            </>
          ) : (
            <div className="result-breakdown">
              <div className="breakdown-row">
                <span>{t('patient.spentOnTests2')}</span>
                <span className="lose">-{formatMoney(totalTestCost)}</span>
              </div>
              <div className="breakdown-row total">
                <span>{t('patient.wrongPenalty')}</span>
                <span className="lose">-{formatMoney(result.penalty)}</span>
              </div>
              {!canPlay && (
                <p className="result-broke-note">{t('patient.outOfFunds')}</p>
              )}
            </div>
          )}
          {result.correct ? (
            <button className="back-to-lobby-btn" onClick={() => navigate('/game')}>
              {t('patient.backToLevels')}
            </button>
          ) : (
            <div className="result-actions">
              {canPlay && (
                <button className="try-again-btn" onClick={handleTryAgain}>
                  {t('patient.tryAgain')}
                </button>
              )}
              <button className="back-to-lobby-btn back-to-lobby-secondary" onClick={() => navigate('/game')}>
                {t('patient.backToLevels')}
              </button>
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="room-container">
      {/* Top bar */}
      <div className="room-topbar">
        <span className="room-back-btn" role="button" tabIndex={0} onClick={() => navigate('/game')} onKeyDown={(e) => e.key === 'Enter' && navigate('/game')}>
          {t('patient.back')}
        </span>
        <div className="room-title">
          <span className="room-title-patient">{patient.name}</span>
          <span className="room-title-sep">·</span>
          <span className="room-title-sub">{t('patient.level', { level: patient.level })}</span>
        </div>
        <div className="room-wallet">
          <span className="wallet-icon">💰</span>
          <span className={`wallet-num ${balance < 0 ? 'negative' : ''}`}>
            {formatMoney(balance)}
          </span>
        </div>
      </div>

      {/* Diagnosis bar — full width, prominent */}
      {isReview ? (
        <div className="diagnosis-bar">
          <div className="diagnosis-bar-inner">
            <span className="diagnosis-bar-label review-mode-label">{t('patient.reviewMode')}</span>
            <span className="review-mode-desc">{t('patient.reviewModeDesc')}</span>
            <button
              className="submit-diagnosis-btn diagnosis-bar-btn"
              onClick={handleRevealAnswer}
              disabled={submitting}
            >
              {submitting ? t('patient.submitting') : t('patient.revealAnswer')}
            </button>
            {submitError && (
              <div className="error-message submit-error-popup">
                <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2" />
                  <path d="M12 8V12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                  <circle cx="12" cy="16" r="1" fill="currentColor" />
                </svg>
                {submitError}
              </div>
            )}
          </div>
        </div>
      ) : (
        <div className="diagnosis-bar">
          <div className="diagnosis-bar-inner">
            <span className="diagnosis-bar-label">{t('patient.yourDiagnosis')}</span>
            <div className="diagnosis-search-container">
              <input
                type="text"
                className={`diagnosis-select diagnosis-bar-select diagnosis-search-input ${selectedDiagnosis ? 'diagnosis-select-filled' : ''}`}
                placeholder={t('patient.selectDiagnosis')}
                value={diagnosisSearch}
                onChange={(e) => {
                  setDiagnosisSearch(e.target.value);
                  setSelectedDiagnosis('');
                  setDiagnosisOpen(true);
                }}
                onFocus={() => setDiagnosisOpen(true)}
                onBlur={() => setTimeout(() => setDiagnosisOpen(false), 150)}
              />
              {diagnosisOpen && filteredDiagnoses.length > 0 && (
                <ul className="diagnosis-dropdown-list">
                  {filteredDiagnoses.map((d) => (
                    <li
                      key={d}
                      className="diagnosis-dropdown-item"
                      onMouseDown={() => handleDiagnosisSelect(d)}
                    >
                      {d}
                    </li>
                  ))}
                </ul>
              )}
            </div>
            <div className="diagnosis-bar-reward">
              <span className="diagnosis-bar-reward-label">{t('patient.rewardIfCorrect')}</span>
              <strong className="reward-amount">💰 {formatMoney(caseReward)}</strong>
            </div>
            <button
              className="submit-diagnosis-btn diagnosis-bar-btn"
              onClick={handleSubmitDiagnosis}
              disabled={!selectedDiagnosis || submitting}
            >
              {submitting ? t('patient.submitting') : t('patient.confirmDiagnosis')}
            </button>
            {submitError && (
              <div className="error-message submit-error-popup">
                <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2" />
                  <path d="M12 8V12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                  <circle cx="12" cy="16" r="1" fill="currentColor" />
                </svg>
                {submitError}
              </div>
            )}
          </div>
        </div>
      )}

      <div className="room-layout">
        {/* Left: Patient info */}
        <aside className="room-left">
          <section className="info-section">
            <div className="patient-header-card">
              <div className="patient-big-avatar">
                {isMale ? '👨' : '👩'}
              </div>
              <div>
                <div className="patient-big-name">{patient.name}</div>
                <div className="patient-big-meta">{t('patient.yearsOld', { age: patient.age })} · {patient.gender}</div>
              </div>
            </div>
          </section>

          <section className="info-section">
            <h4 className="info-label">{upper(t('patient.chiefComplaint'))}</h4>
            <p className="info-text complaint-text">&ldquo;{patient.chiefComplaint}&rdquo;</p>
          </section>

          <section className="info-section">
            <h4 className="info-label">{upper(t('patient.vitals'))}</h4>
            <div className="vitals-grid">
              {Object.entries(patient.vitals).map(([k, v]) => (
                <div key={k} className="vital-item">
                  <span className="vital-key">{k}</span>
                  <span className="vital-val">{v}</span>
                </div>
              ))}
            </div>
          </section>

        </aside>

        {/* Center: Patient chart — all gathered findings */}
        <main className="room-center">
          {orderedTests.length === 0 &&
           (!backgroundQuestions || !backgroundQuestions.some((q) => isQuestionAsked(q.id))) &&
           (!physicalExamItems || !physicalExamItems.some((item) => isExamItemAsked(item.id))) ? (
            <div className="no-tests-placeholder">
              <span>📋</span>
              <p>{t('patient.noFindingsYet')}</p>
            </div>
          ) : (
            <>
              {backgroundQuestions && backgroundQuestions.some((q) => isQuestionAsked(q.id)) && (
                <section className="center-section">
                  <h3 className="section-heading">📋 {upper(t('patient.backgroundHistory'))}</h3>
                  <div className="clinical-items-list">
                    {backgroundQuestions.map((q) =>
                      isQuestionAsked(q.id) ? (
                        <div key={q.id} className={`clinical-item ${(q.id in backgroundAnswersMap && !backgroundAnswersMap[q.id]) ? 'clinical-item-na' : ''}`}>
                          <span className="clinical-item-question">{upper(q.question)}</span>
                          <span className="clinical-item-answer">
                            {q.id in backgroundAnswersMap
                              ? (backgroundAnswersMap[q.id] || t('patient.notApplicable'))
                              : '...'}
                          </span>
                        </div>
                      ) : null
                    )}
                  </div>
                </section>
              )}

              {physicalExamItems && physicalExamItems.some((item) => isExamItemAsked(item.id)) && (
                <section className="center-section">
                  <h3 className="section-heading">🩺 {upper(t('patient.physicalExam'))}</h3>
                  <div className="clinical-items-list">
                    {physicalExamItems.map((item) =>
                      isExamItemAsked(item.id) ? (
                        <div key={item.id} className={`clinical-item ${(item.id in examAnswersMap && !examAnswersMap[item.id]) ? 'clinical-item-na' : ''}`}>
                          <span className="clinical-item-question">{upper(item.question)}</span>
                          <span className="clinical-item-answer">
                            {item.id in examAnswersMap
                              ? (examAnswersMap[item.id] || t('patient.notApplicable'))
                              : '...'}
                          </span>
                        </div>
                      ) : null
                    )}
                  </div>
                </section>
              )}

              {orderedTests.length > 0 && (
                <section className="center-section">
                  <h3 className="section-heading">🔬 {upper(t('patient.testResults'))}</h3>
                  <div className="test-results-list">
                    {orderedTests.map((testId) => (
                      <div key={testId} className="test-result-card">
                        <div className="test-result-header">
                          <span className="test-icon">{tests[testId].icon}</span>
                          <span className="test-result-name">{tests[testId].name}</span>
                          {!isReview && <span className="test-result-cost">-{formatMoney(tests[testId].cost)}</span>}
                          <span className="test-info-btn" role="button" tabIndex={0} onClick={() => setInfoTest(testId)} onKeyDown={(e) => e.key === 'Enter' && setInfoTest(testId)} title={t('patient.referenceRanges')}>ⓘ</span>
                        </div>
                        <p className="test-result-text">{testResultsMap[testId] ?? '...'}</p>
                      </div>
                    ))}
                  </div>
                </section>
              )}
            </>
          )}
        </main>

        {/* Right: Examine patient + Order tests + Background questions */}
        <aside className="room-right">
          {backgroundQuestions && backgroundQuestions.length > 0 && (
            <div className="right-panel-section">
              <h3 className="section-heading">
                📋 {upper(t('patient.backgroundHistory'))}
                {askedQuestions.size > 0 && (
                  <span className="bg-q-count">{askedQuestions.size}/{backgroundQuestions.length}</span>
                )}
              </h3>
              {!isReview && (
                <div className="bg-question-dropdown-row">
                  <select
                    className="bg-question-select"
                    value={selectedQuestionId}
                    onChange={(e) => setSelectedQuestionId(e.target.value)}
                  >
                    <option value="">{t('patient.selectQuestion')}</option>
                    {backgroundQuestions
                      .filter((q) => !askedQuestions.has(q.id))
                      .map((q) => (
                        <option key={q.id} value={q.id}>{q.question}</option>
                      ))
                    }
                  </select>
                  <button
                    className="bg-question-ask-btn"
                    onClick={handleAskQuestion}
                    disabled={!selectedQuestionId}
                  >
                    {t('patient.askQuestion')}
                  </button>
                </div>
              )}
            </div>
          )}

          {physicalExamItems && physicalExamItems.length > 0 && (
            <div className="right-panel-section">
              <h3 className="section-heading">
                🩺 {upper(t('patient.physicalExam'))}
                {askedExamItems.size > 0 && (
                  <span className="bg-q-count">{askedExamItems.size}/{physicalExamItems.length}</span>
                )}
              </h3>
              {!isReview && (
                <div className="bg-question-dropdown-row">
                  <select
                    className="bg-question-select"
                    value={selectedExamItemId}
                    onChange={(e) => setSelectedExamItemId(e.target.value)}
                  >
                    <option value="">{t('patient.selectExamItem')}</option>
                    {physicalExamItems
                      .filter((item) => !askedExamItems.has(item.id))
                      .map((item) => (
                        <option key={item.id} value={item.id}>{item.question}</option>
                      ))
                    }
                  </select>
                  <button
                    className="bg-question-ask-btn"
                    onClick={handleAskExamItem}
                    disabled={!selectedExamItemId}
                  >
                    {t('patient.examine')}
                  </button>
                </div>
              )}
            </div>
          )}

          <div className="right-panel-section">
            <h3 className="section-heading">
              🔬 {upper(t('patient.orderTests'))}
              {orderedTests.length > 0 && (
                <span className="bg-q-count">{orderedTests.length}/{testList.length}</span>
              )}
            </h3>
            {!isReview && <p className="cost-note">{t('patient.spentSoFar')} 💰 {formatMoney(totalTestCost)}</p>}
            {!isReview && (
              <div className="bg-question-dropdown-row">
                <select
                  className="bg-question-select"
                  value={selectedTestId}
                  onChange={(e) => setSelectedTestId(e.target.value)}
                >
                  <option value="">{t('patient.selectTest')}</option>
                  {testList
                    .filter((test) => !orderedTests.includes(test.id))
                    .map((test) => (
                      <option key={test.id} value={test.id}>
                        {test.icon} {test.name}{!isReview ? ` — 💰 ${formatMoney(test.cost)}` : ''}
                      </option>
                    ))
                  }
                </select>
                <button
                  className="bg-question-ask-btn"
                  onClick={handleOrderTestFromDropdown}
                  disabled={!selectedTestId}
                >
                  {t('patient.orderTest')}
                </button>
              </div>
            )}
          </div>

        </aside>
      </div>

      {/* Test info modal */}
      {infoTest && (
        <div className="confirm-overlay" onClick={() => setInfoTest(null)}>
          <div className="confirm-modal test-info-modal" onClick={(e) => e.stopPropagation()}>
            <div className="test-info-header">
              <span>{tests[infoTest].icon}</span>
              <h3>{tests[infoTest].name}</h3>
            </div>
            <div className="test-info-range test-info-normal">
              <span className="test-info-range-label">✅ {t('patient.normalRange')}</span>
              <p>{tests[infoTest].normalRange}</p>
            </div>
            <div className="test-info-range test-info-danger">
              <span className="test-info-range-label">⚠️ {t('patient.dangerousRange')}</span>
              <p>{tests[infoTest].dangerousRange}</p>
            </div>
            <button className="confirm-ok" onClick={() => setInfoTest(null)}>{t('patient.close')}</button>
          </div>
        </div>
      )}

      {/* Confirm background question modal */}
      {confirmQuestion && (
        <div className="confirm-overlay" onClick={() => setConfirmQuestion(null)}>
          <div className="confirm-modal" onClick={(e) => e.stopPropagation()}>
            <h3>{backgroundQuestions.find((q) => q.id === confirmQuestion)?.question}</h3>
            <p className="confirm-cost">
              {t('patient.cost')} <strong>💰 {formatMoney(QUESTION_COST)}</strong>
            </p>
            <p className="confirm-warning">
              {t('patient.ifNotRelevantQuestion')}
            </p>
            <div className="confirm-actions">
              <button className="confirm-cancel" onClick={() => setConfirmQuestion(null)}>
                {t('patient.cancel')}
              </button>
              <button className="confirm-ok" onClick={confirmAskQuestion}>
                {t('patient.askQuestion')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Confirm physical exam modal */}
      {confirmExamItem && (
        <div className="confirm-overlay" onClick={() => setConfirmExamItem(null)}>
          <div className="confirm-modal" onClick={(e) => e.stopPropagation()}>
            <h3>{physicalExamItems.find((item) => item.id === confirmExamItem)?.question}</h3>
            <p className="confirm-cost">
              {t('patient.cost')} <strong>💰 {formatMoney(EXAM_COST)}</strong>
            </p>
            <p className="confirm-warning">
              {t('patient.ifNotRelevantExam')}
            </p>
            <div className="confirm-actions">
              <button className="confirm-cancel" onClick={() => setConfirmExamItem(null)}>
                {t('patient.cancel')}
              </button>
              <button className="confirm-ok" onClick={confirmAskExamItem}>
                {t('patient.examine')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Confirm test modal */}
      {confirmTest && (
        <div className="confirm-overlay" onClick={() => setConfirmTest(null)}>
          <div className="confirm-modal" onClick={(e) => e.stopPropagation()}>
            <h3>{t('patient.orderTestConfirm', { name: tests[confirmTest].name })}</h3>
            <p className="confirm-description">{tests[confirmTest].description}</p>
            <p className="confirm-cost">
              {t('patient.cost')} <strong>💰 {formatMoney(tests[confirmTest].cost)}</strong>
            </p>
            <p className="confirm-warning">
              {t('patient.ifNotRelevant')}
            </p>
            <div className="confirm-actions">
              <button className="confirm-cancel" onClick={() => setConfirmTest(null)}>
                {t('patient.cancel')}
              </button>
              <button className="confirm-ok" onClick={confirmOrderTest}>
                {t('patient.orderTest')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
